import { ImageOff, Link2, Link2Off, VideoOff } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { ResourceSocialPlatformIcon } from 'v5/patterns/resource/ResourceSocialPlatformIcon';
import {
  GalleryEditor,
  type LinkFieldProps
} from 'v5/patterns/widgets/gallery-editor/GalleryEditor';
import { mediaLinkUtils } from 'v5/patterns/widgets/gallery-editor/mediaLink';
import { type PinLinkWrite, pinboardUtils } from './pinboard.logic';

const providerName = { youtube: 'YouTube', vimeo: 'Vimeo' } as const;

/**
 * An address typed into the link field and what it pins: a picture at its proportions, a
 * video's poster, or a web page (any address that loads as no picture), as v1 took any link.
 * Add pins exactly what the preview shows. Returns the field's props and the preview.
 */
export function usePinLink(
  onAdd: (write: PinLinkWrite) => void,
  initialValue = ''
): {
  field: Omit<LinkFieldProps, 'label'>;
  preview: ReactNode;
} {
  const { t } = useTranslation('resources', { keyPrefix: 'Pinboard.link' });
  const [value, setValue] = useState(initialValue);
  const [url] = useDebounce(value.trim(), 250);
  const link = useMemo(
    () => (url === '' ? null : mediaLinkUtils.classify(url)),
    [url]
  );
  const [load, setLoad] = useState<{
    src: string;
    status: 'loaded' | 'failed';
  } | null>(null);

  const media = link === null || link.type === 'notAUrl' ? null : link;
  const src = (() => {
    switch (media?.type) {
      case undefined:
        return null;
      case 'video':
        return media.poster;
      case 'image':
      case 'unknown':
        return media.url;
    }
  })();
  const status =
    src === null ? null : load?.src === src ? load.status : 'loading';
  const settled = url === value.trim();
  const isPage = media?.type === 'unknown' && status === 'failed';
  const canAdd = media !== null && settled && (status === 'loaded' || isPage);

  const problem = (() => {
    if (link === null) return null;
    switch (link.type) {
      case 'notAUrl':
        return {
          icon: <Link2Off className="size-4" />,
          title: t('notAUrl'),
          description: t('notAUrlHint')
        };
      case 'image':
        return status === 'failed'
          ? {
              icon: <ImageOff className="size-4" />,
              title: t('brokenPicture'),
              description: t('brokenPictureHint')
            }
          : null;
      case 'video':
        return status === 'failed'
          ? {
              icon: <VideoOff className="size-4" />,
              title: t('missingVideo'),
              description: t('missingVideoHint')
            }
          : null;
      case 'unknown':
        return null;
    }
  })();

  const clear = () => setValue('');
  const preview = (() => {
    if (value.trim() === '') return null;
    if (problem !== null) {
      return (
        <GalleryEditor.LinkPreview
          type="problem"
          onClear={clear}
          {...problem}
        />
      );
    }
    if (media === null || src === null) return null;
    if (isPage) {
      return (
        // not a problem: a web page pins as a link; the preview's quiet card tells so
        <GalleryEditor.LinkPreview
          type="problem"
          onClear={clear}
          icon={<Link2 className="size-4" />}
          title={t('page')}
          description={new URL(media.url).hostname}
        />
      );
    }
    return (
      <GalleryEditor.LinkPreview
        type="media"
        src={src}
        onClear={clear}
        playable={media.type === 'video'}
        loaded={status === 'loaded'}
        onLoad={() => setLoad({ src, status: 'loaded' })}
        onError={() => setLoad({ src, status: 'failed' })}
        caption={
          media.type === 'video' ? (
            <>
              <ResourceSocialPlatformIcon platform={media.provider} />
              <span className="shrink-0 font-semibold">
                {providerName[media.provider]}
              </span>
            </>
          ) : (
            <span className="min-w-0 truncate text-fg-muted">
              {new URL(media.url).hostname}
            </span>
          )
        }
      />
    );
  })();

  return {
    field: {
      description: t('hint'),
      value,
      onValueChange: setValue,
      canAdd,
      invalid: settled && problem !== null,
      onAdd: () => {
        if (media === null) return;
        onAdd(pinboardUtils.linkWrite(media, status === 'loaded'));
        clear();
      }
    },
    preview
  };
}
